import { type Dispatch, type FC, type ReactNode, type SetStateAction, useEffect, useMemo, useState } from 'react';

import { useSession } from '@eneco/auth/useSession';
import { useSelfServiceAccount } from '@eneco/auth-be-self-service';
import { useApplication } from '@eneco/common/application';
import RichText from '@eneco/components/RichText/RichText';
import {
  getEnecoBeXapiSiteApiV1AccountsByAccountNumberDeliveryAddressesByAddressIdentifierProductSwitch,
  getEnecoBeXapiSiteApiV1AccountsByAccountNumberProductSwitchOrderByOrderId,
  getEnecoBeXapiSiteApiV1ProductSwitchOrderByOrderIdAccountNumber,
  postEnecoBeXapiSiteApiV1AccountsByAccountNumberDeliveryAddressesByAddressIdentifierProductSwitch,
  ProductSwitchRecordDto,
} from '@eneco/dc-be/client';
import useAuthenticatedDCBE from '@eneco/dc-be/hooks/useAuthenticatedDCBE';
import unwrapData from '@eneco/dc-be/utils/unwrapData';
import { typedYupResolver } from '@eneco/hooks/form';
import { useFormatter } from '@eneco/i18n';
import { useRouter } from '@eneco/next';
import {
  AlertDialog,
  Box,
  Button,
  Divider,
  Form,
  Heading,
  NotificationBox,
  PageGrid,
  RadioCard,
  RadioGroup,
  Skeleton,
  Stack,
  Stretch,
  Text,
  TextLink,
} from '@eneco/sparky';
import { styled } from '@eneco/sparky/stitches';
import { NotificationBoxProps } from '@eneco/sparky/types';
import { useTracking } from '@eneco/tracking';
import { useContent } from '@sitecore/common';
import { ProductSwitchRenderingExtended } from '@sitecore/types/manual/be/ProductSwitchExtended';
import { Controller, FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useSWRConfig } from 'swr';
import * as yup from 'yup';

import { ProductSwitchHeading } from './ProductSwitchHeading';
import ProductSwitchMailLandingPage from './ProductSwitchMailLandingPage';
import ProductSwitchOverview from './ProductSwitchOverview';
import getSortedProducts from './utils/getSortedProducts';
import { StyledDisclaimerText } from './utils/StyledDisclaimerText';
import { buildGTMNextButtonPayload, buildGTMSummaryButtonPayload } from './utils/trackGTM';
import { RoundedSkeleton } from '../../../components/Loading/RoundedSkeleton';
import { useAddressOptions } from '../../../hooks/useAddressOptions';
import { useRedirectAndNotifyBE, useSwitchAccountAndRedirectAndNotifyBE } from '../../../hooks/useRedirectAndNotifyBE';
import { productSwitchStackClass, stackGrowClass } from '../../../styles/components.css';
import { ADDRESS_PARAM } from '../../../utils/addSearchParams';
import { getSitecoreProduct } from '../../../utils/getSitecoreProduct';
import { inlineCheckmarkReplacement } from '../../../utils/richTextReplacements';

export const SCHEMA_SELECTED_PRODUCTS_SWITCH_NAMES = 'selectedProductsSwitchNames' as const;
export const SCHEMA_IS_AGREEMENT_CHECKED = 'isAgreementChecked' as const;
export const CURRENT = 'current';

type CompareProductFormValues = { selectedProductsSwitchNames: string[] };

type CompareProductProps = {
  activeProduct: ProductSwitchRecordDto | undefined;
  activeIndexProductSwitch: number;
  isElectricity: boolean;
  isLoadingSubmit: boolean;
  fields: ProductSwitchRenderingExtended['fields'];
  format: (template: string, vars?: Record<string, unknown>) => string;
  selectedProductsSwitchNames: string[];
  control: import('react-hook-form').Control<CompareProductFormValues>;
  trackEventBE: (event: string, payload?: { status?: string; data?: unknown; message?: string }) => void;
  setIsLoadingSubmit: (value: boolean) => void;
  contractIdSearchParam: string | null;
  redirectAndNotify: (notification: { route: string; text: ReactNode; title: string; variant?: string }) => void;
  products: ProductSwitchRecordDto[] | undefined;
  setActiveIndexProductSwitch: Dispatch<SetStateAction<number>>;
  isOpenCancelDialog: boolean;
  setIsOpenCancelDialog: Dispatch<SetStateAction<boolean>>;
  push: (url: string) => void;
  sortedProducts: ProductSwitchRecordDto[];
  locale: string;
};

type ProductSwitchContentProps = {
  sortedProducts: ProductSwitchRecordDto[];
  activeIndexProductSwitch: number;
  editPreviousSelectedProductSwitch: (ean: string) => void;
  isLoadingSubmit: boolean;
  activeProduct: ProductSwitchRecordDto | undefined;
  compareProductProps: CompareProductProps;
};

const CompareProduct: FC<CompareProductProps> = ({
  activeProduct,
  activeIndexProductSwitch,
  isElectricity,
  isLoadingSubmit,
  fields,
  format,
  selectedProductsSwitchNames,
  control,
  trackEventBE,
  setIsLoadingSubmit,
  contractIdSearchParam,
  redirectAndNotify,
  products,
  setActiveIndexProductSwitch,
  isOpenCancelDialog,
  setIsOpenCancelDialog,
  push,
  sortedProducts,
  locale,
}) => {
  const title = useMemo(() => {
    const isUpSelling = activeIndexProductSwitch > 0;
    switch (true) {
      case isUpSelling && isElectricity:
        return fields.electricityStep.upsellTitle.value;
      case isUpSelling && !isElectricity:
        return fields.gasStep.upsellTitle.value;
      case isElectricity:
        return fields.electricityStep.title.value;
      default:
        return fields.gasStep.title.value;
    }
  }, [activeIndexProductSwitch, isElectricity, fields]);

  if (!activeProduct) return null;

  const nextButtonOnClick = () => {
    const gtmPayload = buildGTMNextButtonPayload({
      activeProduct,
      selectedProductsSwitchNames,
      activeIndexProductSwitch,
      CURRENT,
    });
    trackEventBE('be_myeneco_switch_step', {
      status: 'click',
      data: gtmPayload,
    });

    const onlyCurrentProductsSelected = (selectedProductsSwitchNames as string[]).every(
      switchName => switchName === CURRENT,
    );
    if (onlyCurrentProductsSelected && activeIndexProductSwitch === 0) {
      setIsLoadingSubmit(true);
      const cancelRoute = contractIdSearchParam
        ? `${fields.navigation.cancelLink.value.href}?contract=${contractIdSearchParam}`
        : fields.navigation.cancelProductsOverviewLink.value.href;
      redirectAndNotify({
        route: cancelRoute,
        text: <RichText html={fields.navigation.keepCurrentProductNotification.value.content} />,
        title: fields.navigation.keepCurrentProductNotification.value.title,
        variant: fields.navigation.keepCurrentProductNotification.value.variant,
      });
    } else {
      if (activeIndexProductSwitch < (products?.length ?? 0)) {
        setActiveIndexProductSwitch(prev => prev + 1);
      }
    }
  };

  const promoBaseHref = isElectricity
    ? fields.electricityStep.productsLink?.value?.href
    : fields.gasStep.productsLink?.value?.href;
  const promoHref = contractIdSearchParam ? `${promoBaseHref}?contract=${contractIdSearchParam}` : promoBaseHref;
  const promoLinkText = isElectricity
    ? fields.electricityStep.productsLink?.value?.text
    : fields.gasStep.productsLink?.value?.text;
  const promoDisclaimerLink = `<a href="${promoHref}" target="_self">${promoLinkText}</a>`;

  return (
    <>
      <ProductSwitchHeading
        title={<span>{format(title, { step: activeIndexProductSwitch + 1 })}</span>}
        subTitle={format(isElectricity ? fields.electricityStep.subTitle.value : fields.gasStep.subTitle.value, {
          ean: activeProduct.current?.ean ?? '',
        })}
        sortedProducts={sortedProducts}
        stepperLabels={{
          electricityStepLabel: fields.navigation.electricityStepLabel.value,
          gasStepLabel: fields.navigation.gasStepLabel.value,
          summaryStepLabel: fields.navigation.summaryStepLabel.value,
        }}
        activeIndexProductSwitch={activeIndexProductSwitch}
      />

      <Controller
        key={activeIndexProductSwitch}
        control={control}
        name={`selectedProductsSwitchNames.${activeIndexProductSwitch}`}
        defaultValue={CURRENT}
        render={({ field: { onChange, value, name } }) => (
          <RadioGroup name={name} aria-labelledby="productSwitch" isFullWidth value={value} onValueChange={onChange}>
            <Stack direction={{ initial: 'column', md: 'row' }} gap="6">
              {activeProduct?.current && (
                <ProductProposalItem
                  key={activeProduct.current.ean}
                  value={CURRENT}
                  isCurrent={true}
                  id={activeProduct.current.ean || ''}
                  technicalProductName={activeProduct.current.technicalProductName || ''}
                  promoLoss={activeProduct.current.promoLoss}
                  tariffChartUrl={
                    locale === 'fr-BE' ? activeProduct.current.tariffChartUrlFr : activeProduct.current.tariffChartUrlNl
                  }
                />
              )}
              {activeProduct?.switchOptions?.map(item => {
                return (
                  <ProductProposalItem
                    key={item.technicalProductName}
                    value={item.technicalProductName || ''}
                    isCurrent={false}
                    id={item.technicalProductName || ''}
                    technicalProductName={item.technicalProductName || ''}
                    promoLoss={activeProduct.current?.promoLoss}
                    tariffChartUrl={locale === 'fr-BE' ? item.tariffChartUrlFr : item.tariffChartUrlNl}
                  />
                );
              })}
            </Stack>
          </RadioGroup>
        )}
      />

      {activeProduct.current?.promoLoss && (
        <StyledDisclaimerText>
          <Text>
            <RichText
              html={
                isElectricity
                  ? fields.electricityStep.productSwitchDescription?.value
                  : fields.gasStep.productSwitchDescription?.value
              }
            />
          </Text>
          <Text>
            <RichText
              html={format(
                isElectricity
                  ? fields.electricityStep.promoDisclaimerText?.value
                  : fields.gasStep.promoDisclaimerText?.value,
                { link: promoDisclaimerLink },
              )}
            />
          </Text>
        </StyledDisclaimerText>
      )}
      <Stack direction="row" gap={5} wrap>
        <Button isLoading={isLoadingSubmit} onClick={nextButtonOnClick}>
          {selectedProductsSwitchNames?.[activeIndexProductSwitch] === CURRENT
            ? fields.navigation.keepCurrentProductText.value
            : isElectricity
              ? fields.electricityStep.continueButtonText.value
              : fields.gasStep.continueButtonText.value}
        </Button>
        <AlertDialog
          isOpen={isOpenCancelDialog}
          setOpen={setIsOpenCancelDialog}
          confirmText={fields.navigation.cancelDialog.value.submitButtonText ?? ''}
          denyText={fields.navigation.cancelDialog.value.cancelButtonText ?? ''}
          title={fields.navigation.cancelDialog.value.title}
          description={<RichText html={fields.navigation.cancelDialog.value.content} />}
          trigger={<Button action="secondary">{fields.navigation.cancelLink.value.text}</Button>}
          onConfirm={() => {
            const cancelRoute = contractIdSearchParam
              ? `${fields.navigation.cancelLink.value.href}?contract=${contractIdSearchParam}`
              : (fields.navigation?.cancelProductsOverviewLink?.value?.href ?? 'my-eneco/products');
            push(cancelRoute);
          }}
          onDeny={() => {}}
        />
      </Stack>
    </>
  );
};

const ProductSwitchContent: FC<ProductSwitchContentProps> = ({
  sortedProducts,
  activeIndexProductSwitch,
  editPreviousSelectedProductSwitch,
  isLoadingSubmit,
  activeProduct,
  compareProductProps,
}) => {
  return sortedProducts && activeIndexProductSwitch > sortedProducts.length - 1 ? (
    <ProductSwitchOverview
      sortedProducts={sortedProducts}
      editPreviousSelectedProductSwitch={editPreviousSelectedProductSwitch}
      isLoadingSubmit={isLoadingSubmit}
      hasOrderId={false}
    />
  ) : (
    <CompareProduct {...compareProductProps} activeProduct={activeProduct} />
  );
};

const ProductSwitch = () => {
  const { fields } = useContent<ProductSwitchRenderingExtended>();
  const { data: session } = useSession();
  const { selectedAccount, isCurrentAccountReader } = useSelfServiceAccount();
  const { addressIdentifier, isLoading: isLoadingAddressOptions } = useAddressOptions();
  const { format, date } = useFormatter();
  const { searchParams } = useApplication();
  const { mutate } = useSWRConfig();
  const redirectAndNotify = useRedirectAndNotifyBE();
  const switchAccountRedirectAndNotify = useSwitchAccountAndRedirectAndNotifyBE();
  const { push } = useRouter();
  const { locale } = useApplication();
  const { trackEventBE } = useTracking();

  const orderIdSearchParam = searchParams.get('orderId') ?? searchParams.get('orderid');
  const contractIdSearchParam = searchParams.get('contract');
  const eanSearchParam = searchParams.get('ean');

  const [isLoadingSubmit, setIsLoadingSubmit] = useState<boolean>(false);
  const [activeIndexProductSwitch, setActiveIndexProductSwitch] = useState<number>(0);
  const [error, setError] = useState<NotificationBoxProps | null>(null);
  const [isOpenCancelDialog, setIsOpenCancelDialog] = useState<boolean>(false);

  const { data: productSwitchOptionsWrapped } = useAuthenticatedDCBE(
    getEnecoBeXapiSiteApiV1AccountsByAccountNumberDeliveryAddressesByAddressIdentifierProductSwitch,
    { path: { accountNumber: selectedAccount.crmAccountNumber, addressIdentifier: addressIdentifier } },
    [`/accounts/${selectedAccount.crmAccountNumber}/delivery-addresses/${addressIdentifier}/product-switch`],
    session,
  );

  const productSwitchOptions = unwrapData(productSwitchOptionsWrapped)?.records;

  const { data: productSwitchAccountWrapped, isLoading: productSwitchAccountIsLoading } = useAuthenticatedDCBE(
    getEnecoBeXapiSiteApiV1ProductSwitchOrderByOrderIdAccountNumber,
    { path: { orderId: orderIdSearchParam ?? '' } },
    [`/product-switch/order/${orderIdSearchParam ?? ''}`],
    session,
    { shouldFetch: !!orderIdSearchParam },
  );

  const productSwitchAccountNr = unwrapData(productSwitchAccountWrapped)?.accountNumber;
  const productSwitchEan = unwrapData(productSwitchAccountWrapped)?.ean;
  const productSwitchDeliveryAddressId = unwrapData(productSwitchAccountWrapped)?.deliveryAddressId;

  const { data: dataWrapped, isLoading: isLoadingData } = useAuthenticatedDCBE(
    getEnecoBeXapiSiteApiV1AccountsByAccountNumberProductSwitchOrderByOrderId,
    {
      path: {
        accountNumber: productSwitchAccountNr ?? selectedAccount.crmAccountNumber,
        orderId: orderIdSearchParam ?? '',
      },
    },
    [
      `/accounts/${selectedAccount.crmAccountNumber}/product-switch/order/${orderIdSearchParam ?? ''}`,
      `/accounts/${productSwitchAccountNr}/product-switch/order/${orderIdSearchParam ?? ''}`,
    ],
    session,
    {
      shouldFetch: !!productSwitchAccountNr && !!orderIdSearchParam && !productSwitchAccountIsLoading,
    },
  );

  const isLoading = isLoadingData || isLoadingAddressOptions;

  const data = unwrapData(dataWrapped);

  const products = data?.records ?? productSwitchOptions;

  const sortedProducts = useMemo(() => {
    return getSortedProducts(products, eanSearchParam, isCurrentAccountReader);
  }, [products, eanSearchParam, isCurrentAccountReader]);

  const activeProduct: ProductSwitchRecordDto | undefined = sortedProducts?.[activeIndexProductSwitch];
  const isElectricity = activeProduct?.current?.energyType === 'Electricity';
  const firstDayOfNextMonth = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 1);
  const firstDayOfNextMonthFormatted = `${date.dayOfMonthNumeric(firstDayOfNextMonth)} ${date.monthLong(firstDayOfNextMonth)} ${date.year(firstDayOfNextMonth)}`;

  type FormValues = yup.InferType<typeof FormSchema>;

  const FormSchema = yup.object({
    [SCHEMA_SELECTED_PRODUCTS_SWITCH_NAMES]: yup.array().of(yup.string().required('error')).required('error'),
    [SCHEMA_IS_AGREEMENT_CHECKED]: yup
      .boolean()
      .oneOf([true], fields.summaryStep.termsAndConditionsCheckboxFormField.value.validationMessage),
  });

  const resolver = typedYupResolver<FormValues>(FormSchema);

  const form = useForm<FormValues>({
    resolver,
    mode: 'onBlur',
    defaultValues: {
      selectedProductsSwitchNames: [],
    },
  });

  const { control, handleSubmit, watch, setValue } = form;
  const selectedProductsSwitchNames = watch('selectedProductsSwitchNames');

  const submitForm: SubmitHandler<FormValues> = async ({ selectedProductsSwitchNames }) => {
    try {
      setIsLoadingSubmit(true);
      const toProductTechnicalNames = orderIdSearchParam
        ? sortedProducts.map(sp => sp.switchOptions?.[0]?.technicalProductName ?? '')
        : selectedProductsSwitchNames;
      const switchNamesAttachedEans = sortedProducts.map((sp, index) => ({
        ean: productSwitchEan ?? sp.current?.ean ?? '',
        toProductTechnicalName: toProductTechnicalNames?.[index],
      }));
      const actualSwitchNamesAttachedEans = switchNamesAttachedEans.filter(x => x.toProductTechnicalName !== CURRENT);
      const GTMPayload = buildGTMSummaryButtonPayload({
        CURRENT,
        productSwitchNames: toProductTechnicalNames,
        sortedProducts,
      });
      const { response } =
        await postEnecoBeXapiSiteApiV1AccountsByAccountNumberDeliveryAddressesByAddressIdentifierProductSwitch({
          path: {
            accountNumber: productSwitchAccountNr ?? selectedAccount.crmAccountNumber,
            addressIdentifier: productSwitchDeliveryAddressId ?? addressIdentifier,
          },
          body: {
            records: actualSwitchNamesAttachedEans,
            orderId: orderIdSearchParam ?? '',
          },
        });
      if (!response.ok) {
        setIsLoadingSubmit(false);
        trackEventBE('be_myeneco_switch_confirmed', { status: 'failed', data: GTMPayload });
        return setError({
          isAlert: false,
          text: <RichText html={fields.navigation.errorNotification.value.content} />,
          title: fields.navigation.errorNotification.value.title,
          variant: fields.navigation.errorNotification.value.variant,
        });
      }
      await mutate([`/accounts/${selectedAccount.crmAccountNumber}/products/switch`]);
      trackEventBE('be_myeneco_switch_confirmed', { status: 'success', data: GTMPayload });
      if (selectedAccount.crmAccountNumber === productSwitchAccountNr || !productSwitchAccountNr) {
        redirectAndNotify({
          route: `${fields.navigation.saveLink.value.href}?${ADDRESS_PARAM}=${addressIdentifier}`,
          text: (
            <RichText
              html={format(fields.navigation.successNotification.value.content, {
                date: firstDayOfNextMonthFormatted,
              })}
            />
          ),
          title: fields.navigation.successNotification.value.title,
          variant: fields.navigation.successNotification.value.variant,
        });
      } else {
        switchAccountRedirectAndNotify({
          crmAccountNumber: productSwitchAccountNr,
          route: `${fields.navigation.saveLink.value.href}?${ADDRESS_PARAM}=${addressIdentifier}`,
          text: (
            <RichText
              html={format(fields.navigation.successNotification.value.content, {
                date: firstDayOfNextMonthFormatted,
              })}
            />
          ),
          title: fields.navigation.successNotification.value.title,
          variant: fields.navigation.successNotification.value.variant,
        });
      }
    } catch (e) {
      void e;
      setIsLoadingSubmit(false);
      trackEventBE('be_myeneco_switch_confirmed', { status: 'failed', message: 'unexpected error occurred' });
      return setError({
        isAlert: false,
        text: <RichText html={fields.navigation.errorNotification.value.content} />,
        title: fields.navigation.errorNotification.value.title,
        variant: fields.navigation.errorNotification.value.variant,
      });
    }
  };

  const editPreviousSelectedProductSwitch = (ean: string) => {
    const index = sortedProducts?.findIndex(p => p.current?.ean === ean);
    if (index != null) setActiveIndexProductSwitch(index);
  };

  useEffect(() => {
    sortedProducts.forEach((_, index) => {
      setValue(`selectedProductsSwitchNames.${index}`, CURRENT, { shouldDirty: false });
    });
  }, [sortedProducts, setValue]);

  useEffect(() => {
    if (!isLoading && data && !sortedProducts.length && !orderIdSearchParam) {
      setIsLoadingSubmit(true);
      const cancelRoute = contractIdSearchParam
        ? `${fields.navigation.cancelLink.value.href}?contract=${contractIdSearchParam}`
        : (fields.navigation?.cancelProductsOverviewLink?.value?.href ?? 'my-eneco/products');
      redirectAndNotify({
        route: cancelRoute,
        text: <RichText html={fields.navigation.keepCurrentProductNotification.value.content} />,
        title: fields.navigation.keepCurrentProductNotification.value.title,
        variant: fields.navigation.keepCurrentProductNotification.value.variant,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, data, sortedProducts]);

  const compareProductProps = {
    activeProduct,
    activeIndexProductSwitch,
    isElectricity,
    isLoadingSubmit,
    fields,
    format,
    selectedProductsSwitchNames,
    control,
    trackEventBE,
    setIsLoadingSubmit,
    contractIdSearchParam,
    redirectAndNotify,
    products,
    setActiveIndexProductSwitch,
    isOpenCancelDialog,
    setIsOpenCancelDialog,
    push,
    sortedProducts,
    locale,
  } as CompareProductProps;
  return (
    <FormProvider {...form}>
      <Form onSubmit={handleSubmit(submitForm)}>
        <Box paddingY={{ initial: '4', md: '12' }}>
          <PageGrid>
            <PageGrid.Item gridColumn="1/-1">
              <Stack gap="10" alignX={'start'}>
                {orderIdSearchParam ? (
                  <ProductSwitchMailLandingPage orderId={orderIdSearchParam} />
                ) : (
                  <>
                    {isLoading ? (
                      <CompareProductSkeletons />
                    ) : (
                      products && (
                        <ProductSwitchContent
                          sortedProducts={sortedProducts}
                          activeIndexProductSwitch={activeIndexProductSwitch}
                          editPreviousSelectedProductSwitch={editPreviousSelectedProductSwitch}
                          isLoadingSubmit={isLoadingSubmit}
                          activeProduct={activeProduct}
                          compareProductProps={compareProductProps}
                        />
                      )
                    )}
                    {error && <NotificationBox {...error} />}
                  </>
                )}
              </Stack>
            </PageGrid.Item>
          </PageGrid>
        </Box>
      </Form>
    </FormProvider>
  );
};

type ProductProposalItemProps = {
  isCurrent: boolean;
  id: string;
  value: string;
  technicalProductName: string;
  promoLoss?: boolean;
  tariffChartUrl: string | null | undefined;
};

const HiddenRibbonContainer = styled('div', {
  display: 'none',
  '@md': {
    display: 'block',
    visibility: 'hidden',
  },
});

const ProductProposalItem: FC<ProductProposalItemProps> = ({
  isCurrent,
  id,
  value,
  technicalProductName,
  promoLoss,
  tariffChartUrl,
}) => {
  const { fields } = useContent<ProductSwitchRenderingExtended>();
  const { trackEventBE } = useTracking();

  const sitecoreProduct = getSitecoreProduct({
    technicalProductName: technicalProductName,
    enecoProductsList: fields.productSwitchProposal.enecoProductsList,
  });
  const productFeatures = sitecoreProduct?.productFeaturesContent?.value;
  const asterisk = promoLoss && !isCurrent ? '*' : null;

  return (
    <Stack className={productSwitchStackClass}>
      {!isCurrent && (
        // this is a workaround to hide the ribbon when the product is not current, but still have the correct height
        <HiddenRibbonContainer aria-hidden={true}>
          <RadioCard.Ribbon emphasis={'medium'}>{fields.productProposal.currentProductLabel.value}</RadioCard.Ribbon>
        </HiddenRibbonContainer>
      )}
      <Stack.Item grow>
        <RadioCard key={id} value={value} ariaLabelledby={id}>
          {isCurrent && <RadioCard.Ribbon>{fields.productProposal.currentProductLabel.value}</RadioCard.Ribbon>}
          <Stack>
            <Box padding="6">
              <Stack gap="2">
                <Stack direction={{ initial: 'column', lg: 'row' }} gap="3">
                  <Stack direction={'row'} gap="3" alignY={'center'}>
                    <RadioCard.Indicator />
                    <Heading size="XS" as="h3">
                      {sitecoreProduct?.productDisplayName?.value ?? ''} {asterisk}
                    </Heading>
                  </Stack>
                </Stack>
                <RichText html={productFeatures} replacements={inlineCheckmarkReplacement} />
              </Stack>
            </Box>
            <Stack.Item grow />
            {tariffChartUrl && (
              <>
                <Divider />
                <Box padding="4">
                  <Stack alignX={'center'}>
                    <TextLink
                      href={tariffChartUrl}
                      onClick={() => {
                        trackEventBE('be_myeneco_tariffcard_click', {
                          status: 'click',
                          data: { technicalProductName, isSummary: false },
                        });
                      }}
                      target="_blank">
                      {isCurrent
                        ? fields.productProposal.currentProductLabel.value
                        : fields.productProposal.rateCardLinkLabel.value}
                    </TextLink>
                  </Stack>
                </Box>
              </>
            )}
          </Stack>
        </RadioCard>
      </Stack.Item>
    </Stack>
  );
};

export default ProductSwitch;

const HiddenLoadingText = styled(Text, {
  visibility: 'hidden',
  paddingTop: '$3',
  paddingBottom: '$3',
});

const CompareProductSkeletons = () => {
  return (
    <Stretch>
      <Stack gap="10">
        <Stretch>
          <Box backgroundColor="backgroundPrimary">
            <HiddenLoadingText aria-hidden={true}>Loading...</HiddenLoadingText>
          </Box>
        </Stretch>
        <Stack gap="2">
          <Skeleton width={150} height={32} />
          <Skeleton />
          <Skeleton />
          <Skeleton width={250} />
        </Stack>

        <Stack className={stackGrowClass} direction={{ initial: 'column', lg: 'row' }} gap={10} wrap>
          <Stack.Item grow>
            <RoundedSkeleton height={250} />
          </Stack.Item>
          <Stack.Item grow>
            <RoundedSkeleton height={250} />
          </Stack.Item>
          <Stack.Item grow>
            <RoundedSkeleton height={250} />
          </Stack.Item>
        </Stack>
      </Stack>
    </Stretch>
  );
};
